import React, { useState, useMemo, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import './AnalyticsDashboard.css';

const COLORS = ['#000000', '#333333', '#666666', '#999999', '#cccccc'];

const formatCurrency = (value) => `${Number(value || 0).toFixed(2)} ر.ق`;

const getOrderDate = (order) => {
  if (!order?.createdAt) return null;
  if (typeof order.createdAt?.toDate === 'function') {
    return order.createdAt.toDate();
  }
  const parsed = new Date(order.createdAt);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getCustomerKey = (order) => {
  const email = order.customer?.email?.toLowerCase().trim();
  if (email) return email;
  const phone = order.customer?.phone?.replace(/\s+/g, '');
  if (phone) return phone;
  const name = `${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`.trim();
  return name || null;
};

const isPaidOrder = (order) => order.paymentStatus === 'paid' || order.status === 'paid';

function AnalyticsDashboard({ orders, products }) {
  const [dateRange, setDateRange] = useState('30'); // days: '7', '30', '90', 'all'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [topViewedProducts, setTopViewedProducts] = useState([]);
  const [isLoadingViews, setIsLoadingViews] = useState(false);

  // Calculate date range
  const getDateRange = () => {
    let endDate = new Date();
    let startDate = new Date();

    if (dateRange === 'custom') {
      if (customStartDate && customEndDate) {
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
        if (endDate < startDate) {
          [startDate, endDate] = [endDate, startDate];
        }
      }
    } else if (dateRange === '7') {
      startDate.setDate(endDate.getDate() - 7);
    } else if (dateRange === '30') {
      startDate.setDate(endDate.getDate() - 30);
    } else if (dateRange === '90') {
      startDate.setDate(endDate.getDate() - 90);
    } else {
      startDate = new Date(0); // All time
    }

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    const rangeDays = Math.max(
      1,
      Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    );

    return { startDate, endDate, rangeDays };
  };

  const dateRangeBounds = useMemo(() => getDateRange(), [dateRange, customStartDate, customEndDate]);

  // Filter orders by date range
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const orderDate = getOrderDate(order);
      if (!orderDate) return false;
      return orderDate >= dateRangeBounds.startDate && orderDate <= dateRangeBounds.endDate;
    });
  }, [orders, dateRangeBounds]);

  const paidOrders = useMemo(
    () => filteredOrders.filter(order => isPaidOrder(order)),
    [filteredOrders]
  );

  const unpaidOrders = useMemo(
    () => filteredOrders.filter(order => !isPaidOrder(order)),
    [filteredOrders]
  );

  // Calculate metrics
  const metrics = useMemo(() => {
    const totalSales = paidOrders.reduce((sum, order) => sum + (order.totals?.total || 0), 0);
    const pendingValue = unpaidOrders.reduce((sum, order) => sum + (order.totals?.total || 0), 0);
    const totalOrders = filteredOrders.length;
    const paidOrderCount = paidOrders.length;
    const pendingOrderCount = unpaidOrders.length;
    const cancelledOrders = filteredOrders.filter(order => {
      const status = (order.status || '').toLowerCase();
      return status === 'cancelled' || status === 'canceled';
    }).length;
    const averageOrderValue = paidOrderCount > 0 ? totalSales / paidOrderCount : 0;
    const totalItems = paidOrders.reduce((sum, order) => 
      sum + (order.items?.reduce((itemSum, item) => itemSum + item.quantity, 0) || 0), 0
    );
    const averageItemsPerOrder = paidOrderCount > 0 ? totalItems / paidOrderCount : 0;
    const highestOrderValue = paidOrders.reduce((max, order) => 
      Math.max(max, order.totals?.total || 0), 0
    );
    const lowestOrderValue = paidOrders.length > 0
      ? paidOrders.reduce((min, order) => Math.min(min, order.totals?.total || 0), Infinity)
      : 0;

    return {
      totalSales,
      pendingValue,
      totalOrders,
      paidOrderCount,
      pendingOrderCount,
      cancelledOrders,
      averageOrderValue,
      totalItems,
      averageItemsPerOrder,
      highestOrderValue,
      lowestOrderValue,
    };
  }, [filteredOrders, paidOrders, unpaidOrders]);

  const customerFirstOrderMap = useMemo(() => {
    const map = new Map();
    orders.forEach(order => {
      if (!isPaidOrder(order)) return;
      const key = getCustomerKey(order);
      const orderDate = getOrderDate(order);
      if (!key || !orderDate) return;
      const existing = map.get(key);
      if (!existing || orderDate < existing) {
        map.set(key, orderDate);
      }
    });
    return map;
  }, [orders]);

  const customerInsights = useMemo(() => {
    const map = new Map();
    paidOrders.forEach(order => {
      const key = getCustomerKey(order);
      const orderDate = getOrderDate(order);
      if (!key || !orderDate) return;
      const name = `${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`.trim() || 'Customer';
      const entry = map.get(key) || {
        key,
        name,
        email: order.customer?.email || '',
        phone: order.customer?.phone || '',
        orders: 0,
        revenue: 0,
        firstOrder: orderDate,
        lastOrder: orderDate,
      };
      entry.orders += 1;
      entry.revenue += order.totals?.total || 0;
      if (orderDate < entry.firstOrder) entry.firstOrder = orderDate;
      if (orderDate > entry.lastOrder) entry.lastOrder = orderDate;
      map.set(key, entry);
    });

    const customers = Array.from(map.values());
    const repeatCustomers = customers.filter(customer => customer.orders > 1).length;
    const uniqueCustomers = customers.length;
    const repeatRate = uniqueCustomers > 0 ? (repeatCustomers / uniqueCustomers) * 100 : 0;

    const topCustomers = [...customers]
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    const newCustomers = customers.filter(customer => {
      const firstOrder = customerFirstOrderMap.get(customer.key);
      return firstOrder && firstOrder >= dateRangeBounds.startDate && firstOrder <= dateRangeBounds.endDate;
    }).length;

    const returningCustomers = customers.filter(customer => {
      const firstOrder = customerFirstOrderMap.get(customer.key);
      return firstOrder && firstOrder < dateRangeBounds.startDate;
    }).length;

    return {
      customers,
      uniqueCustomers,
      repeatCustomers,
      repeatRate,
      topCustomers,
      newCustomers,
      returningCustomers,
    };
  }, [paidOrders, customerFirstOrderMap, dateRangeBounds]);

  const previousPeriod = useMemo(() => {
    if (dateRange === 'all') return null;
    const rangeMs = dateRangeBounds.endDate.getTime() - dateRangeBounds.startDate.getTime();
    const prevEnd = new Date(dateRangeBounds.startDate.getTime() - 1);
    const prevStart = new Date(prevEnd.getTime() - rangeMs);
    const prevOrders = orders.filter(order => {
      const orderDate = getOrderDate(order);
      if (!orderDate) return false;
      return orderDate >= prevStart && orderDate <= prevEnd;
    });
    const prevPaidOrders = prevOrders.filter(order => isPaidOrder(order));
    const prevSales = prevPaidOrders.reduce((sum, order) => sum + (order.totals?.total || 0), 0);
    return {
      prevSales,
      prevOrders: prevOrders.length,
    };
  }, [orders, dateRange, dateRangeBounds]);

  const salesChange = previousPeriod && previousPeriod.prevSales > 0
    ? ((metrics.totalSales - previousPeriod.prevSales) / previousPeriod.prevSales) * 100
    : null;

  const ordersChange = previousPeriod && previousPeriod.prevOrders > 0
    ? ((metrics.totalOrders - previousPeriod.prevOrders) / previousPeriod.prevOrders) * 100
    : null;

  const buildTimeSeries = (sourceOrders) => {
    const { startDate, endDate, rangeDays } = dateRangeBounds;
    const groupBy = rangeDays <= 7 ? 'day' : rangeDays <= 30 ? 'day' : rangeDays <= 90 ? 'week' : 'month';
    const dataMap = new Map();

    if (groupBy === 'day') {
      for (let i = 0; i < Math.min(rangeDays, 30); i++) {
        const date = new Date(endDate);
        date.setDate(date.getDate() - i);
        const key = date.toISOString().split('T')[0];
        dataMap.set(key, { date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), sales: 0, orders: 0 });
      }
    } else if (groupBy === 'week') {
      for (let i = 0; i < Math.min(Math.ceil(rangeDays / 7), 12); i++) {
        const date = new Date(endDate);
        date.setDate(date.getDate() - (i * 7));
        const key = `Week ${i + 1}`;
        dataMap.set(key, { date: key, sales: 0, orders: 0 });
      }
    } else {
      for (let i = 0; i < Math.min(Math.ceil(rangeDays / 30), 12); i++) {
        const date = new Date(endDate);
        date.setMonth(date.getMonth() - i);
        const key = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        dataMap.set(key, { date: key, sales: 0, orders: 0 });
      }
    }

    sourceOrders.forEach(order => {
      const orderDate = getOrderDate(order);
      if (!orderDate) return;
      let key;

      if (groupBy === 'day') {
        key = orderDate.toISOString().split('T')[0];
      } else if (groupBy === 'week') {
        const weekNum = Math.floor((endDate - orderDate) / (1000 * 60 * 60 * 24 * 7));
        key = `Week ${weekNum + 1}`;
      } else {
        key = orderDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }

      if (dataMap.has(key)) {
        const entry = dataMap.get(key);
        entry.sales += order.totals?.total || 0;
        entry.orders += 1;
      }
    });

    return Array.from(dataMap.values()).reverse();
  };

  const salesOverTime = useMemo(
    () => buildTimeSeries(paidOrders),
    [paidOrders, dateRangeBounds]
  );

  const ordersOverTime = useMemo(
    () => buildTimeSeries(filteredOrders),
    [filteredOrders, dateRangeBounds]
  );

  // Payment methods data
  const paymentMethodsData = useMemo(() => {
    const methods = {};
    paidOrders.forEach(order => {
      const method = order.paymentMethod || 'unknown';
      methods[method] = (methods[method] || 0) + 1;
    });

    return Object.entries(methods).map(([name, value]) => ({
      name: name === 'cash' ? 'Cash on Delivery' : 
            name === 'card' ? 'Card Payment' : 
            name === 'manual_link' ? 'Manual Link' : 
            name.charAt(0).toUpperCase() + name.slice(1),
      value
    }));
  }, [paidOrders]);

  // Order status data
  const orderStatusData = useMemo(() => {
    const statuses = {};
    filteredOrders.forEach(order => {
      const status = order.status || 'pending';
      statuses[status] = (statuses[status] || 0) + 1;
    });

    return Object.entries(statuses).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value
    }));
  }, [filteredOrders]);

  // Top products data
  const topProducts = useMemo(() => {
    const productMap = {};
    paidOrders.forEach(order => {
      order.items?.forEach(item => {
        const productName = item.name || 'Unknown';
        productMap[productName] = {
          name: productName,
          quantity: (productMap[productName]?.quantity || 0) + item.quantity,
          revenue: (productMap[productName]?.revenue || 0) + (item.price * item.quantity)
        };
      });
    });

    return Object.values(productMap)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  }, [paidOrders]);

  const productById = useMemo(() => {
    return new Map(products.map(product => [product.id, product]));
  }, [products]);

  const productNameById = useMemo(() => {
    return new Map(products.map(product => [product.id, product.name]));
  }, [products]);

  const categoryRevenueData = useMemo(() => {
    const categoryMap = {};
    paidOrders.forEach(order => {
      order.items?.forEach(item => {
        const product = productById.get(item.id);
        const category = product?.category || 'Uncategorized';
        const revenue = (item.price || 0) * (item.quantity || 0);
        categoryMap[category] = (categoryMap[category] || 0) + revenue;
      });
    });

    return Object.entries(categoryMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [paidOrders, productById]);

  const sizeData = useMemo(() => {
    const sizeMap = {};
    paidOrders.forEach(order => {
      order.items?.forEach(item => {
        if (!item.size) return;
        sizeMap[item.size] = (sizeMap[item.size] || 0) + (item.quantity || 0);
      });
    });

    return Object.entries(sizeMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [paidOrders]);

  const topLocations = useMemo(() => {
    const locationMap = {};
    paidOrders.forEach(order => {
      const city = order.shipping?.city || order.shipping?.shippingMethodName || '';
      const country = order.shipping?.country || '';
      const locationLabel = [city, country].filter(Boolean).join(', ') || 'Unknown';
      if (!locationMap[locationLabel]) {
        locationMap[locationLabel] = { name: locationLabel, orders: 0, revenue: 0 };
      }
      locationMap[locationLabel].orders += 1;
      locationMap[locationLabel].revenue += order.totals?.total || 0;
    });

    return Object.values(locationMap)
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 5);
  }, [paidOrders]);

  useEffect(() => {
    let isMounted = true;

    const fetchProductViews = async () => {
      setIsLoadingViews(true);
      try {
        const viewsRef = collection(db, 'productViews');
        let snapshot;
        if (dateRange === 'all' || (dateRange === 'custom' && (!customStartDate || !customEndDate))) {
          snapshot = await getDocs(viewsRef);
        } else {
          snapshot = await getDocs(query(viewsRef, where('viewedAt', '>=', dateRangeBounds.startDate)));
        }

        const viewMap = new Map();
        snapshot.forEach(docSnap => {
          const data = docSnap.data();
          if (!data.productId) return;
          const viewedAt = data.viewedAt?.toDate ? data.viewedAt.toDate() : null;
          if (viewedAt && viewedAt > dateRangeBounds.endDate) return;

          const existing = viewMap.get(data.productId) || {
            productId: data.productId,
            productName: data.productName || productNameById.get(data.productId) || 'Unknown product',
            views: 0,
          };
          existing.views += 1;
          viewMap.set(data.productId, existing);
        });

        const topViews = Array.from(viewMap.values())
          .sort((a, b) => b.views - a.views)
          .slice(0, 5);

        if (isMounted) {
          setTopViewedProducts(topViews);
        }
      } catch (error) {
        console.error('Failed to fetch product views:', error);
        if (isMounted) {
          setTopViewedProducts([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingViews(false);
        }
      }
    };

    fetchProductViews();

    return () => {
      isMounted = false;
    };
  }, [dateRange, customStartDate, customEndDate, dateRangeBounds, productNameById]);

  const hasData = filteredOrders.length > 0 || topViewedProducts.length > 0;

  return (
    <div className="analytics-dashboard">
      <div className="analytics-header">
        <div>
          <h1>Analytics Dashboard</h1>
          <p>Track your store performance and sales metrics</p>
        </div>
        <div className="date-range-selector">
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(e.target.value)}
            className="date-range-select"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All time</option>
            <option value="custom">Custom range</option>
          </select>
          {dateRange === 'custom' && (
            <div className="custom-date-inputs">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="date-input"
              />
              <span>to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="date-input"
              />
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
              <line x1="7" y1="7" x2="7.01" y2="7"></line>
            </svg>
          </div>
          <div className="summary-content">
            <div className="summary-label">Total Products</div>
            <div className="summary-value">{products.length}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
            </svg>
          </div>
          <div className="summary-content">
            <div className="summary-label">Featured Products</div>
            <div className="summary-value">{products.filter(p => p.featured).length}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
          <div className="summary-content">
            <div className="summary-label">Unique Customers</div>
            <div className="summary-value">{customerInsights.uniqueCustomers}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <div className="summary-content">
            <div className="summary-label">New Customers</div>
            <div className="summary-value">{customerInsights.newCustomers}</div>
          </div>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="metrics-grid">
        <div className="metric-card-analytics">
          <div className="metric-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <div className="metric-content">
            <div className="metric-label">Total Sales</div>
            <div className="metric-value">{formatCurrency(metrics.totalSales)}</div>
            {salesChange !== null && (
              <div className={`metric-trend ${salesChange >= 0 ? 'positive' : 'negative'}`}>
                {salesChange >= 0 ? '▲' : '▼'} {Math.abs(salesChange).toFixed(1)}% vs previous period
              </div>
            )}
          </div>
        </div>
        <div className="metric-card-analytics">
          <div className="metric-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <path d="M16 10a4 4 0 0 1-8 0"></path>
            </svg>
          </div>
          <div className="metric-content">
            <div className="metric-label">Total Orders</div>
            <div className="metric-value">{metrics.totalOrders}</div>
            {ordersChange !== null && (
              <div className={`metric-trend ${ordersChange >= 0 ? 'positive' : 'negative'}`}>
                {ordersChange >= 0 ? '▲' : '▼'} {Math.abs(ordersChange).toFixed(1)}% vs previous period
              </div>
            )}
          </div>
        </div>
        <div className="metric-card-analytics">
          <div className="metric-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <div className="metric-content">
            <div className="metric-label">Paid Orders</div>
            <div className="metric-value">{metrics.paidOrderCount}</div>
          </div>
        </div>
        <div className="metric-card-analytics">
          <div className="metric-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <div className="metric-content">
            <div className="metric-label">Unpaid Orders</div>
            <div className="metric-value">{metrics.pendingOrderCount}</div>
          </div>
        </div>
        <div className="metric-card-analytics">
          <div className="metric-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
          </div>
          <div className="metric-content">
            <div className="metric-label">Average Order Value</div>
            <div className="metric-value">{formatCurrency(metrics.averageOrderValue)}</div>
          </div>
        </div>
        <div className="metric-card-analytics">
          <div className="metric-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <path d="M16 10a4 4 0 0 1-8 0"></path>
            </svg>
          </div>
          <div className="metric-content">
            <div className="metric-label">Items Sold</div>
            <div className="metric-value">{metrics.totalItems}</div>
          </div>
        </div>
        <div className="metric-card-analytics">
          <div className="metric-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="21" x2="4" y2="14"></line>
              <line x1="9" y1="21" x2="9" y2="10"></line>
              <line x1="14" y1="21" x2="14" y2="6"></line>
              <line x1="19" y1="21" x2="19" y2="2"></line>
            </svg>
          </div>
          <div className="metric-content">
            <div className="metric-label">Avg Items / Order</div>
            <div className="metric-value">{metrics.averageItemsPerOrder.toFixed(2)}</div>
          </div>
        </div>
        <div className="metric-card-analytics">
          <div className="metric-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <div className="metric-content">
            <div className="metric-label">Pending Value</div>
            <div className="metric-value">{formatCurrency(metrics.pendingValue)}</div>
          </div>
        </div>
        <div className="metric-card-analytics">
          <div className="metric-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
          <div className="metric-content">
            <div className="metric-label">Repeat Customers</div>
            <div className="metric-value">{customerInsights.repeatCustomers}</div>
            <div className="metric-subvalue">
              {customerInsights.repeatRate.toFixed(0)}% repeat rate
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      {hasData ? (
        <div className="charts-section">
        <div className="charts-grid">
          {/* Sales Over Time */}
          <div className="chart-card">
            <h3>Sales Over Time</h3>
            {salesOverTime.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={salesOverTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Sales']}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="sales" 
                    stroke="#000000" 
                    strokeWidth={2}
                    name="Sales (ر.ق)"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="chart-empty">No data available for selected date range</div>
            )}
          </div>

        {/* Orders Over Time */}
        <div className="chart-card">
          <h3>Orders Over Time</h3>
          {ordersOverTime.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ordersOverTime}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="orders" fill="#333333" name="Orders" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-empty">No data available for selected date range</div>
          )}
        </div>

        {/* Payment Methods */}
        <div className="chart-card">
          <h3>Payment Methods</h3>
          {paymentMethodsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={paymentMethodsData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {paymentMethodsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-empty">No paid payments in selected range</div>
          )}
        </div>

        {/* Order Status */}
        <div className="chart-card">
          <h3>Order Status Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={orderStatusData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="#000000" name="Orders" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top Products */}
        <div className="chart-card">
          <h3>Top Products by Revenue</h3>
          {topProducts.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topProducts} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={150} />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), 'Revenue']}
                />
                <Legend />
                <Bar dataKey="revenue" fill="#000000" name="Revenue (ر.ق)" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-empty">No data available for selected date range</div>
          )}
        </div>

        {/* Top Products by Quantity */}
        <div className="chart-card">
          <h3>Top Products by Quantity</h3>
          {topProducts.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topProducts}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="quantity" fill="#666666" name="Quantity Sold" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-empty">No data available for selected date range</div>
          )}
        </div>
        <div className="chart-card">
          <h3>Revenue by Category</h3>
          {categoryRevenueData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={categoryRevenueData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={140} />
                <Tooltip formatter={(value) => [formatCurrency(value), 'Revenue']} />
                <Legend />
                <Bar dataKey="value" fill="#000000" name="Revenue (ر.ق)" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-empty">No category revenue yet</div>
          )}
        </div>
        <div className="chart-card">
          <h3>Popular Sizes</h3>
          {sizeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={sizeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#666666" name="Items Sold" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-empty">No size data available</div>
          )}
        </div>
        </div>

        <div className="insights-grid">
          <div className="chart-card">
            <h3>Top Customers</h3>
            {customerInsights.topCustomers.length > 0 ? (
              <div className="chart-list">
                {customerInsights.topCustomers.map(customer => (
                  <div key={customer.key} className="chart-list-item">
                    <div className="chart-list-left">
                      <div className="chart-list-title">{customer.name}</div>
                      <div className="chart-list-sub">
                        {customer.email || customer.phone || 'No contact info'}
                      </div>
                    </div>
                    <div className="chart-list-metric">
                      {formatCurrency(customer.revenue)}
                      <span>{customer.orders} order{customer.orders !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="chart-empty">No customer data yet</div>
            )}
          </div>
          <div className="chart-card">
            <h3>Top Locations</h3>
            {topLocations.length > 0 ? (
              <div className="chart-list">
                {topLocations.map(location => (
                  <div key={location.name} className="chart-list-item">
                    <div className="chart-list-left">
                      <div className="chart-list-title">{location.name}</div>
                      <div className="chart-list-sub">{location.orders} order{location.orders !== 1 ? 's' : ''}</div>
                    </div>
                    <div className="chart-list-metric">
                      {formatCurrency(location.revenue)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="chart-empty">No location data yet</div>
            )}
          </div>
          <div className="chart-card">
            <h3>Top Viewed Products</h3>
            {isLoadingViews ? (
              <div className="chart-empty">Loading view data...</div>
            ) : topViewedProducts.length > 0 ? (
              <div className="chart-list">
                {topViewedProducts.map(product => (
                  <div key={product.productId} className="chart-list-item">
                    <div className="chart-list-left">
                      <div className="chart-list-title">{product.productName}</div>
                      <div className="chart-list-sub">Product ID: {product.productId}</div>
                    </div>
                    <div className="chart-list-metric">
                      {product.views} view{product.views !== 1 ? 's' : ''}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="chart-empty">No view data yet</div>
            )}
          </div>
        </div>
        </div>
      ) : (
        <div className="no-data-message">
          <div className="no-data-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
          </div>
          <h3>No Orders Found</h3>
          <p>There are no orders in the selected date range. Try selecting a different time period.</p>
        </div>
      )}
    </div>
  );
}

export default AnalyticsDashboard;
